/**
 * ChatContext — lets any page/component programmatically open
 * the ChatWidget and optionally pre-fill a message, or navigate
 * to the Support panel with a specific tutorial highlighted.
 *
 * Usage (chat):
 *   const { openChatWithMessage } = useChatContext();
 *   openChatWithMessage("Walk me through building a strategy.");
 *
 * Usage (tutorial highlight):
 *   const { pendingTutorial, clearPendingTutorial } = useChatContext();
 *   // PageHelpBubble navigates to /chat with location.state = { tab: "tutorials", tutorialId: "06" }
 *   // ChatPage reads location.state directly — no context needed for routing.
 */

import React, { createContext, useContext, useState, useCallback } from "react";

const ChatContext = createContext({
  openChatWithMessage: () => {},
  pendingMessage: null,
  clearPendingMessage: () => {},
});

export function ChatProvider({ children }) {
  const [pendingMessage, setPendingMessage] = useState(null);

  const openChatWithMessage = useCallback((text) => {
    setPendingMessage(text);
  }, []);

  const clearPendingMessage = useCallback(() => {
    setPendingMessage(null);
  }, []);

  return (
    <ChatContext.Provider value={{ openChatWithMessage, pendingMessage, clearPendingMessage }}>
      {children}
    </ChatContext.Provider>
  );
}

export function useChatContext() {
  return useContext(ChatContext);
}
